import { useEffect, useMemo, useRef, useState } from "react";
import { IoCheckmark, IoClose, IoSearchOutline } from "react-icons/io5";

export const splitTagFilter = (value) => (value ? String(value).split("*").filter(Boolean) : []);

export const joinTagFilter = (names) => (Array.isArray(names) ? names.filter(Boolean).join("*") : "");

/**
 * Searchable multi-select tag list. Matching is OR (any selected tag).
 */
const TagFilterList = ({
	tags = [],
	selected = [],
	onChange,
	autoFocusSearch = false,
	showClear = true,
	listClassName = "max-h-64",
	className = "",
	searchPlaceholder = "Search tags...",
	searchLabel = "Search tags",
	hint = "Matching any selected tag",
	clearLabel = "Clear tags",
	emptyMessage = "No tags yet",
	noMatchMessage = "No tags match your search",
	remote = false,
	hasMore = false,
	onLoadMore,
	isLoadingMore = false,
	onSearchChange,
}) => {
	const [query, setQuery] = useState("");
	const searchRef = useRef(null);

	useEffect(() => {
		if (autoFocusSearch) {
			searchRef.current?.focus();
		}
	}, [autoFocusSearch]);

	useEffect(() => {
		onSearchChange?.(query);
	}, [onSearchChange, query]);

	const availableTags = useMemo(() => {
		const known = new Set(tags.map((tag) => tag.name));
		const selectedOnly = selected
			.filter((name) => name && !known.has(name))
			.map((name) => ({ id: `selected-${name}`, name }));
		return [...selectedOnly, ...tags];
	}, [tags, selected]);

	// Rows are pinned using the selection as it stood when the list opened or the
	// search changed, so ticking a box never reshuffles rows under the pointer.
	const [pinned, setPinned] = useState(selected);
	const [pinnedQuery, setPinnedQuery] = useState(query);

	if (pinnedQuery !== query) {
		setPinnedQuery(query);
		setPinned(selected);
	}

	const filteredTags = useMemo(() => {
		const q = query.trim().toLowerCase();
		const list = remote || !q
			? availableTags
			: availableTags.filter((tag) => tag.name?.toLowerCase().includes(q));

		// Server-paged lists keep API order so appended pages don't reshuffle rows.
		return [...list].sort((a, b) => {
			const aSel = pinned.includes(a.name) ? 0 : 1;
			const bSel = pinned.includes(b.name) ? 0 : 1;
			if (aSel !== bSel) return aSel - bSel;
			if (remote) return 0;
			return (b.books_count || 0) - (a.books_count || 0);
		});
	}, [availableTags, pinned, query, remote]);

	const toggleTag = (name) => {
		const next = selected.includes(name)
			? selected.filter((tag) => tag !== name)
			: [...selected, name];
		onChange(next);
	};

	const hasSelection = selected.length > 0;

	return (
		<div className={className}>
			<div className="p-2 border-b border-gray-100">
				<div className="relative">
					<IoSearchOutline
						className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400"
						size={15}
					/>
					<input
						ref={searchRef}
						type="search"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") event.preventDefault();
						}}
						placeholder={searchPlaceholder}
						aria-label={searchLabel}
						className="w-full h-9 rounded-lg bg-gray-50 border border-gray-200 pl-8 pr-2 text-sm focus:outline-none focus:ring-1 focus:ring-akgreen focus:border-akgreen"
					/>
				</div>
				<p className="mt-1.5 px-0.5 text-xs text-gray-500">{hint}</p>
			</div>

			<ul className={`${listClassName} overflow-y-auto py-1`} role="listbox" aria-multiselectable="true">
				{filteredTags.length === 0 && (
					<li className="px-3 py-6 text-center text-sm text-gray-500">
						{availableTags.length === 0 ? emptyMessage : noMatchMessage}
					</li>
				)}
				{filteredTags.map((tag) => {
					const isSelected = selected.includes(tag.name);
					return (
						<li key={tag.id}>
							<button
								type="button"
								role="option"
								aria-selected={isSelected}
								onClick={() => toggleTag(tag.name)}
								className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors ${
									isSelected
										? "bg-akgreener bg-opacity-5 text-akgreenest font-medium"
										: "text-gray-700 hover:bg-gray-50"
								}`}
							>
								<span className="flex items-center gap-2 min-w-0">
									<span
										className={`flex-shrink-0 h-4 w-4 rounded border flex items-center justify-center ${
											isSelected
												? "bg-akgreener border-akgreener text-white"
												: "border-gray-300 bg-white"
										}`}
									>
										{isSelected && <IoCheckmark size={12} />}
									</span>
									<span className="truncate">{tag.name}</span>
								</span>
								{typeof tag.books_count === "number" && (
									<span className="flex-shrink-0 text-xs text-gray-400">{tag.books_count}</span>
								)}
							</button>
						</li>
					);
				})}
			</ul>

			{hasMore && (
				<div className="border-t border-gray-100 px-2 py-1.5">
					<button
						type="button"
						onClick={onLoadMore}
						disabled={isLoadingMore}
						className="w-full rounded px-2 py-1.5 text-xs font-semibold text-akgreenest hover:bg-gray-50 disabled:opacity-50"
					>
						{isLoadingMore ? "Loading…" : "Load more tags"}
					</button>
				</div>
			)}

			{showClear && hasSelection && (
				<div className="border-t border-gray-100 px-2 py-1.5">
					<button
						type="button"
						onClick={() => onChange([])}
						className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded"
					>
						<IoClose size={14} />
						{clearLabel}
					</button>
				</div>
			)}
		</div>
	);
};

export default TagFilterList;
